import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { animeList } from "@/mocks/anime";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { useImagePreview } from "@/components/feature/ImagePreviewProvider";
import AnimeGrid from "@/components/feature/AnimeGrid";
import SectionHeading from "@/components/base/SectionHeading";
import VideoPlayer from "@/pages/detail/components/VideoPlayer";
import EpisodeList from "@/pages/detail/components/EpisodeList";
import { formatRating, formatViews } from "@/utils/format";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const anime = animeList.find((item) => item.id === id);
  const [episode, setEpisode] = useState(1);
  const { toggleFavorite, isFavorite } = useFavorites();
  const { openPreview } = useImagePreview();

  useSeo({
    title: anime ? `${anime.title} - 详情介绍 - 樱花动漫网站` : "作品未找到 - 樱花动漫网站",
    description: anime
      ? `${anime.title}（${anime.year}年·${anime.region}）剧情简介：${anime.description.slice(0, 70)}…… 樱花动漫网站提供作品详情、剧集列表与相关推荐。`
      : "樱花动漫网站作品详情页。",
    keywords: anime
      ? `${anime.title},${anime.genres.join(",")},${anime.title}在线观看,樱花动漫网站动漫详情`
      : "樱花动漫网站",
  });

  const related = useMemo(() => {
    if (!anime) return [];
    return animeList
      .filter((item) => item.id !== anime.id)
      .map((item) => ({
        item,
        score:
          item.genres.filter((g) => anime.genres.includes(g)).length * 10 +
          item.rating,
      }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 8)
      .map((entry) => entry.item);
  }, [anime]);

  if (!anime) {
    return (
      <div className="mx-auto flex max-w-xl flex-col items-center justify-center px-4 py-24 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-2xl text-secondary-500">
          <i className="ri-file-unknow-line" />
        </span>
        <h1 className="mt-4 font-heading text-xl font-bold text-foreground-950">
          未找到该动漫作品
        </h1>
        <p className="mt-2 text-sm text-foreground-500">
          你访问的作品可能已下架或链接有误。
        </p>
        <Link
          to="/category"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" />
          返回分类页
        </Link>
      </div>
    );
  }

  const favorited = isFavorite(anime.id);

  const metaRows = [
    { label: "类型", value: anime.genres.join(" / ") },
    { label: "地区", value: anime.region },
    { label: "年代", value: `${anime.year} 年` },
    { label: "总集数", value: `${anime.episodes} 集` },
    { label: "更新状态", value: anime.status },
    { label: "制作", value: anime.studio },
  ];

  return (
    <div className="mx-auto px-4 py-6 md:px-8 md:py-8">
      <nav className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="transition-colors hover:text-primary-700">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link to="/category" className="transition-colors hover:text-primary-700">
          分类
        </Link>
        <i className="ri-arrow-right-s-line" />
        <Link
          to={`/category?genre=${encodeURIComponent(anime.genres[0])}`}
          className="transition-colors hover:text-primary-700"
        >
          {anime.genres[0]}
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="truncate text-foreground-900">{anime.title}</span>
      </nav>

      <h1 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
        {anime.title}
      </h1>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <VideoPlayer title={anime.title} episode={episode} poster={anime.banner} />

          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <EpisodeList total={anime.episodes} current={episode} onChange={setEpisode} />
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-file-text-line text-primary-600" />
              剧情简介
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{anime.description}</p>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="overflow-hidden rounded-lg border border-background-200/70 bg-background-50">
            <button
              type="button"
              onClick={() => openPreview({ src: anime.cover, title: anime.title })}
              className="block w-full"
              aria-label={`放大预览《${anime.title}》封面`}
            >
              <img
                src={anime.cover}
                alt={`${anime.title} 动漫封面`}
                title={`${anime.title} 樱花动漫在线观看`}
                className="w-full object-cover object-top"
                style={{ aspectRatio: "3 / 4" }}
              />
            </button>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 font-heading text-lg font-bold text-accent-700">
                  <i className="ri-star-fill" />
                  {formatRating(anime.rating)}
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-500">
                  <i className="ri-play-circle-line text-primary-500" />
                  {formatViews(anime.views)} 次播放
                </span>
              </div>

              <button
                type="button"
                onClick={() => toggleFavorite(anime.id)}
                className={`mt-4 flex w-full items-center justify-center gap-2 rounded-md py-3 text-sm font-semibold transition-colors ${
                  favorited
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                {favorited ? "已收藏（点击取消）" : "收藏这部动漫"}
              </button>
              <p className="mt-2 flex items-center justify-center gap-1 text-center text-xs text-foreground-400">
                <i className="ri-information-line" />
                收藏保存在本地浏览器
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-information-line text-primary-600" />
              作品信息
            </h3>
            <dl className="mt-3 space-y-3">
              {metaRows.map((row) => (
                <div key={row.label} className="flex items-start gap-3 text-sm">
                  <dt className="w-16 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="min-w-0 flex-1 text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 border-t border-background-200 pt-4">
              <h4 className="text-sm font-semibold text-foreground-900">
                <a href="#related" className="transition-colors hover:text-primary-700">
                  所属分类
                </a>
              </h4>
              <div className="mt-2 flex flex-wrap gap-2">
                {anime.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/category?genre=${encodeURIComponent(genre)}`}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-secondary-200"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/70 bg-primary-50/50 p-5">
            <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
              <i className="ri-lightbulb-line text-accent-600" />
              追番小贴士
            </h3>
            <ul className="mt-3 space-y-2 text-sm text-foreground-600">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                点击剧集列表可切换不同集数。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                收藏后可在同一浏览器再次找到本作。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" />
                播放功能待接入，目前为演示界面。
              </li>
            </ul>
          </div>
        </aside>
      </div>

      <section id="related" className="mt-10 md:mt-14">
        <SectionHeading
          eyebrow="为你推荐"
          title="相关动漫推荐"
          description={`根据《${anime.title}》的类型与热度，为你挑选的相似作品。`}
          actionText="浏览更多"
          actionTo={`/category?genre=${encodeURIComponent(anime.genres[0])}`}
        />
        <AnimeGrid items={related} columns={4} />
      </section>
    </div>
  );
}